import { useState } from "react";
import {
  Stack,
  TextField,
  Button,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Switch,
  FormControlLabel,
  CircularProgress,
  Box,
} from "@mui/material";
import { runFeature } from "@/llm/features";
import { LlmClientError } from "@/llm/client";
import { featureNeedsCompose } from "@/office/integration";
import { CurrentEmailPreview, type CurrentEmail } from "@/components/CurrentEmailPreview";
import type { FeatureId, LlmConfig, ToneStyle } from "@/types";

const TONES: ToneStyle[] = ["formal", "friendly", "concise", "apologetic", "assertive"];
const LANGS = ["English", "Spanish", "French", "German", "Japanese", "Chinese", "Portuguese", "Arabic"];

const HEADERS: Record<FeatureId, string> = {
  draft: "Draft a reply or new message",
  translate: "Translate email or selected text",
  summarize: "Summarize this email / thread",
  tone: "Rewrite tone",
  proofread: "Proofread & fix grammar",
};

/** Features that have a pasteable text input the email body can fill. */
const TEXT_FEATURES: FeatureId[] = ["translate", "tone", "proofread"];

export function FeaturePanel({
  feature,
  config,
}: {
  feature: FeatureId;
  config: LlmConfig;
}) {
  const [instruction, setInstruction] = useState("");
  const [text, setText] = useState("");
  const [targetLang, setTargetLang] = useState("English");
  const [tone, setTone] = useState<ToneStyle>("formal");
  const [apply, setApply] = useState(true);
  const [output, setOutput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<CurrentEmail | null>(null);
  const needsCompose = featureNeedsCompose(feature);

  async function handleRun() {
    setBusy(true);
    setError(null);
    setOutput("");
    try {
      const result = await runFeature(feature, config, {
        instruction,
        text,
        lang: targetLang,
        tone,
        apply,
      });
      setOutput(result);
    } catch (e: unknown) {
      if (e instanceof LlmClientError) {
        setError(`${e.isNetwork ? "Network: " : ""}${e.message}`);
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError(String(e));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(output);
    } catch {
      /* clipboard may be blocked inside the iframe */
    }
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{HEADERS[feature]}</Typography>

      {/* The currently-open email, loaded on demand from the Office body API. */}
      <CurrentEmailPreview
        email={email}
        onLoaded={setEmail}
        busy={busy}
        {...(TEXT_FEATURES.includes(feature) ? { onUseInText: setText } : {})}
      />

      {feature === "draft" && (
        <TextField
          multiline
          minRows={3}
          placeholder="e.g. Decline the meeting politely and propose next Thursday."
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          size="small"
          fullWidth
        />
      )}

      {TEXT_FEATURES.includes(feature) && (
        <TextField
          multiline
          minRows={4}
          placeholder="Paste text here, or load the current email above."
          value={text}
          onChange={(e) => setText(e.target.value)}
          size="small"
          fullWidth
        />
      )}

      {feature === "summarize" && (
        <Typography variant="caption" color="text.secondary">
          Summarize reads the currently-open email automatically — click{" "}
          <b>Load current email</b> above to preview what will be sent.
        </Typography>
      )}

      {feature === "translate" && (
        <FormControl size="small" fullWidth>
          <InputLabel>Target language</InputLabel>
          <Select
            value={targetLang}
            label="Target language"
            onChange={(e) => setTargetLang(e.target.value)}
          >
            {LANGS.map((l) => (
              <MenuItem key={l} value={l}>{l}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {feature === "tone" && (
        <FormControl size="small" fullWidth>
          <InputLabel>Tone</InputLabel>
          <Select
            value={tone}
            label="Tone"
            onChange={(e) => setTone(e.target.value as ToneStyle)}
          >
            {TONES.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="contained" onClick={handleRun} disabled={busy}>
          {busy ? "Working…" : "Run"}
        </Button>
        {needsCompose && (
          <FormControlLabel
            control={
              <Switch
                checked={apply}
                onChange={(e) => setApply(e.target.checked)}
                size="small"
              />
            }
            label={`Apply to email${feature === "tone" || feature === "proofread" ? " (at cursor)" : " (replace body)"}`}
            sx={{ ".MuiFormControlLabel-label": { fontSize: 13 } }}
          />
        )}
        {output && (
          <Button variant="text" onClick={handleCopy}>Copy</Button>
        )}
      </Stack>

      {busy && <CircularProgress size={16} />}

      {error && (
        <Typography color="error" variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {error}
        </Typography>
      )}

      {output && (
        <>
          <Typography variant="caption">Output</Typography>
          <Box
            sx={{
              whiteSpace: "pre-wrap",
              bgcolor: "background.default",
              p: 1,
              borderRadius: 1,
              minHeight: 60,
            }}
          >
            {output}
          </Box>
        </>
      )}
    </Stack>
  );
}
